// Dev-only stub manifest — exercises the renderer against real layout ids until
// Anvil's frozen JSON lands. Crops are computed as center-crop-to-slot-AR (the
// render contract's no-face fallback), so the crop path is realistic and
// distortion-free. NOT shipped: the real reader fetches GET /a/:shareToken.
import type { AlbumManifest, Crop, ManifestPage, FilterId } from "./manifest";
import { getLayout } from "./layouts";

// Square source photos (picsum) so we can crop to any slot AR without upscaling.
const DIM = 1280;
function photoUrl(seed: string, size: number) {
  return `https://picsum.photos/seed/${seed}/${size}/${size}`;
}

/** Center-crop of a WxH image down to a target aspect ratio (contract fallback
 *  when no face is detected). Returns normalized [0,1] crop. */
function centerCrop(imgAR: number, slotAR: number): Crop {
  if (imgAR > slotAR) {
    const w = slotAR / imgAR;
    return { x: (1 - w) / 2, y: 0, w, h: 1 };
  }
  const h = imgAR / slotAR;
  return { x: 0, y: (1 - h) / 2, w: 1, h };
}

interface PageSpec {
  layout: string;
  look?: FilterId;
}

// One page per representative layout, to eyeball every geometry family.
const PAGE_SPECS: PageSpec[] = [
  { layout: "solo" },
  { layout: "duo-split" },
  { layout: "stack" },
  { layout: "cinema" }, // dark background layout
  { layout: "feature" },
  { layout: "triptych", look: "mono" },
  { layout: "grid" },
  { layout: "journal", look: "sepia" },
  { layout: "six" },
  { layout: "nine" },
  { layout: "solo-frame" },
];

export function buildStubManifest(): AlbumManifest {
  const photos: AlbumManifest["photos"] = {};
  const pages: ManifestPage[] = [];
  let photoCounter = 0;

  PAGE_SPECS.forEach((spec, pageIndex) => {
    const layout = getLayout(spec.layout);
    if (!layout) return;
    const slots = layout.slots.map((slot) => {
      const id = `p${photoCounter++}`;
      photos[id] = {
        w: DIM,
        h: DIM,
        srcset: { thumb: photoUrl(id, 320), page: photoUrl(id, DIM) },
        alt: "",
      };
      const slotAR = slot.width / slot.height;
      return {
        slotIndex: slot.index,
        photoId: id,
        crop: centerCrop(DIM / DIM, slotAR),
      };
    });
    pages.push({ index: pageIndex, layout: spec.layout, slots, look: spec.look });
  });

  const coverId = `cover0`;
  photos[coverId] = {
    w: DIM,
    h: DIM,
    srcset: { thumb: photoUrl(coverId, 320), page: photoUrl(coverId, DIM) },
    alt: "Album cover",
  };

  return {
    id: "stub-album",
    title: "A Weekend in Belek",
    subtitle: null,
    sender: { name: "Sara" },
    // §6 photo cover with a baked, auto-coloured, shadowed title over the photo.
    cover: {
      background: {
        type: "photo",
        photoId: coverId,
        crop: { x: 0, y: 0.05, w: 1, h: 0.9 },
        layout: "bleed",
      },
      title: {
        text: "A Weekend in Belek",
        fontId: "fraunces",
        weight: 400,
        // realistic clamped size (single line fits, as the iOS editor guarantees)
        sizePt: 34,
        position: { x: 0.5, y: 0.82 },
        align: "center",
        color: "auto",
        shadow: true,
        plate: null,
      },
      showTitle: true,
    },
    coverLook: "none",
    pageCount: pages.length,
    layoutLibraryVersion: "1.0",
    pages,
    photos,
    joinCode: "K7M2QX",
    print: { available: false, checkoutUrl: null },
    permissions: { download: false, comment: false, reshare: false },
    version: "stub:1",
    updatedAt: "2026-08-05T00:00:00.000Z",
  };
}
